import React from "react";
import { Check } from "lucide-react";
import { M } from "./series";
import { G, Card, KV, Mono, Row, Dot, Donut, Count, LineChart, StackBars, Legend, Glyph, NumBadge, LIME, GREEN, GREEN_2, OFFWHITE, CORAL, INK } from "./kit";
import { PitchLink } from "./slidesA";

const PHASE_TABS = ["Day 0–30", "Day 31–90", "Months 4–6", "Months 7–12", "Months 13–24"];
const PHASES = [
  {
    n: 14, title: <>Day 0–30: BRND DIRECT goes live, <span className="lime">plus 10 beta users.</span></>,
    lead: "Verqo runs BRND DIRECT’s real orders with its current customers; the first friends-and-family users test it free.",
    acts: [["BRND DIRECT live with its current customers", "CEO"], ["Free beta seats: friends & family", "CEO"], ["Credit check on every buyer", "Head of Ops"], ["Bank + card payment on every invoice", "Head of Ops"], ["Ship and insure through Verqo", "Head of Ops"], ["Weekly KPI sheet; fix top 3 frictions", "Data & Automation"]],
    gate: [["Active sellers", "5 (BRND + 4 beta)"], ["Active buyers", "62"], ["Monthly GMV", "$2.7M"], ["BRND customers on Verqo", "1 of 3"], ["Beta seats filled", "4 of 10"], ["Buyers reordering", "~3x / month"]],
    spend: "Core team of 5 · opex $112K in M1 · bank + shipping program fees $16K / mo",
  },
  {
    n: 15, title: <>Day 31–90: the 30+ network joins, <span className="lime">financing goes live.</span></>,
    lead: "Zev’s personal network onboards (~2 sellers a month); Net terms and instant payouts switch on in month 2.",
    acts: [["Personal asks to the 30+ network", "CEO"], ["Switch on Net-60/90 + instant payouts", "CEO + lending partner"], ["Insurance partners: first intros", "CEO"], ["Each seller brings its buyers, free", "Head of Ops"], ["Beta users → paid plans at M4", "Growth & partnerships"], ["Credit limits + weekly loss review", "CEO + CCO"]],
    gate: [["Active sellers", "16"], ["Active buyers", "357"], ["Monthly GMV", "$15.7M"], ["GMV on terms", "7.5%"], ["Beta seats filled", "10 of 10"], ["Credit-loss reserve", "0.30% of financed"]],
    spend: "Growth & partnerships lead joins M3 · opex ~$127K / mo",
  },
  {
    n: 16, title: <>Months 4–6: the Jewish wholesale community, <span className="lime">plus beauty.</span></>,
    lead: "Density in one community we belong to: Jewish private-label and F&B sellers, alongside beauty via BRND. Partners switch on.",
    acts: [["Jewish private-label + F&B sellers", "CEO + Sales AE"], ["Beauty brands from BRND’s suppliers", "Sales AE"], ["Shipping + 3PL partners live", "Growth & partnerships"], ["CFOs refer · 25% of SaaS, 2 yrs", "CEO"], ["Community events + 2 case studies", "Growth & partnerships"], ["Shipping & insurance in checkout", "Head of Ops"]],
    gate: [["Active sellers", "34"], ["Active buyers", "885"], ["Monthly GMV", "$39.3M"], ["Monthly revenue", "$150K"], ["Partners live", "10"], ["GMV on terms", "15%"]],
    spend: "AE #1 joins M4 · risk analyst M6 · opex $160K / mo",
  },
  {
    n: 17, title: <>Months 7–12: partners scale it; <span className="lime">sales and shows add to it.</span></>,
    lead: "Partners become the largest source of new sellers; direct sales, paid marketing and the first trade show add on top.",
    acts: [["Lending brokers bring buyers needing terms", "CEO"], ["23 partners live across 5 types", "Growth & partnerships"], ["Cosmoprof North America (Jul ’27)", "Sales AE"], ["Paid marketing, $8K / month", "Marketing"], ["Direct sales works the pipeline", "Sales AE"], ["Corporate cards live (M9)", "Engineering"]],
    gate: [["Active sellers", "116"], ["Active buyers", "3,555"], ["Monthly GMV", "$159M"], ["Monthly revenue", "$663K"], ["GMV on terms", "25%"], ["Partner-sourced new sellers", "36%"]],
    spend: "Headcount 13 by M12 · opex $222K / mo",
  },
  {
    n: 18, title: <>Months 13–24: partners at scale, <span className="lime">profitable, raise the A.</span></>,
    lead: "Year 2 on the same drivers: 51 partners, 2 AEs, 2 shows. Monthly GMV $159M → $524M.",
    acts: [["Partner program to 51 partners", "Growth & partnerships"], ["AE #2 · Expo West · Cosmoprof NA", "Sales AE #2"], ["Banking for wholesale + cards scale", "Engineering"], ["Terms at 25%, losses ≤ 0.3%", "Credit & risk"], ["Data room: cohorts, vintages, NRR", "CEO + Finance"], ["Run the Series A on slide 26", "CEO"]],
    gate: [["Active sellers", "346"], ["Active buyers", "11,535"], ["Monthly GMV", "$524M"], ["Monthly revenue", "$2.2M"], ["Year-2 EBITDA", "$8.3M"], ["CAC payback", "0.8 months"]],
    spend: "Headcount 19 by M24 · Year-2 opex $3.5M", last: true,
  },
];

const Phase = ({ p, idx }) => (
  <G n={p.n} eyebrow={`EXECUTION PLAN · PHASE ${idx + 1} OF 5`} size={28} title={p.title}
    tagline={<Row style={{ gap: 11, justifyContent: "flex-end" }}>
      {PHASE_TABS.map((t, i) => (
        <span key={t} style={{ color: i === idx ? LIME : "#43544b" }}>{t}</span>
      ))}
    </Row>}>
    <div className="an" style={{ position: "absolute", left: 43.2, top: 102, width: 600, fontSize: 12.5, color: "#9fb0a6", lineHeight: "17px", "--d": "180ms" }}>{p.lead}</div>
    {p.acts.map(([t, who], i) => (
      <Card key={t} d={240 + i * 85} style={{ position: "absolute", left: 43.2 + (i % 3) * 200, top: 148 + Math.floor(i / 3) * 126, width: 188, height: 110, padding: "13px 14px" }}>
        <Row style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <NumBadge n={i + 1} size={24} d={300 + i * 85} />
          <Glyph kind="star" size={26} d={340 + i * 85} />
        </Row>
        <div className="h" style={{ fontSize: 12, marginTop: 10, lineHeight: "15.6px" }}>{t}</div>
        <Mono size={8.5} color={LIME} style={{ position: "absolute", bottom: 13 }}>{who}</Mono>
      </Card>
    ))}
    <Card d={320} accent style={{ position: "absolute", left: 660, top: 140, width: 256.8, height: 266, padding: "16px 18px" }}>
      <Mono size={9} color={LIME}>{p.last ? "EXIT GATE · MONTH 24 TARGETS" : "EXIT GATE · MODEL TARGETS"}</Mono>
      <div style={{ marginTop: 6 }}>
        {p.gate.map(([k, v], i) => (
          <KV key={k} k={k} v={v} d={420 + i * 75} lime={i === 2} />
        ))}
      </div>
    </Card>
    <div className="an" style={{ position: "absolute", left: 660, top: 412, width: 256.8, "--d": "840ms" }}>
      <Mono size={9} color="#7d8f85">TEAM &amp; SPEND · MODEL</Mono>
      <div style={{ fontSize: 9.5, color: "#9fb0a6", marginTop: 10, lineHeight: "13px" }}>{p.spend}</div>
    </div>
  </G>
);

const fmtM = (v) => (v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : `$${Math.round(v / 1e6)}M`);
const Y1 = [["Nov '26", "BRND live: storefront, KYB, pay-ins", "BRND + 4 beta · ACH insurance on", "5 sellers", "5", "$2.7M"], ["Dec '26", "Net terms + instant payouts + shipping", "Network asks start", "First financed order", "10", "$7.8M"], ["Jan '27", "Invoice financing (get paid today)", "Insurance intros · Jewish niche", "10 / 10 beta · 3 BRND buyers", "16", "$15.7M"], ["Feb '27", "Dropship app", "Shipping, 3PL, CFO partners · AE #1", "7 beta → paid", "18", "$19.1M"], ["Mar '27", "Catalog tools + supplier discovery", "Lending brokers", "25 sellers", "25", "$28.4M"], ["Apr '27", "QuickBooks + Shopify apps", "Risk analyst · niche 2.5 / mo", "$150K revenue / mo", "34", "$39.3M"], ["May '27", "Buyer credit view v2 (limits)", "Paid marketing $8K / mo", "18% of GMV on terms", "45", "$53.7M"], ["Jun '27", "EDI connectors", "Niche at 4 sellers / mo", "First EBITDA-positive month", "58", "$71.0M"], ["Jul '27", "Corporate + virtual cards", "Cosmoprof NA booth", "71 sellers", "71", "$90.5M"], ["Aug '27", "Banking for wholesale (wallet)", "Partner manager #2", "25% of GMV on terms", "87", "$112.9M"], ["Sep '27", "AI PO parsing", "23 partners live", "3,041 buyers", "102", "$135.7M"], ["Oct '27", "Partner portal + referral tracking", "Marketing lead", "116 sellers · $1.7M ARR", "116", "$158.9M"]];
const Y2 = [["Nov '27", "Annual plans (10% off)", "AE #2 · reserve accordion", "$183M GMV / mo", "132", "$182.9M"], ["Dec '27", "Partner API", "16 CFO partners", "150 sellers", "150", "$208.6M"], ["Jan '28", "Cohort + credit dashboards", "Engineer #4", "5,273 buyers", "168", "$236.6M"], ["Feb '28", "Multi-warehouse shipping", "Partner yield review", "$266M GMV / mo", "187", "$265.9M"], ["Mar '28", "Card rewards for buyers", "Expo West booth", "206 sellers", "206", "$295.9M"], ["Apr '28", "Series A data room", "Credit analyst #2", "$3.5M ARR", "227", "$327.9M"], ["May '28", "Credit limits by vintage", "45 partners live", "249 sellers", "249", "$361.0M"], ["Jun '28", "Next-vertical catalog templates", "Series A process", "$395M GMV / mo", "270", "$395.3M"], ["Jul '28", "Payout scheduling", "Cosmoprof NA booth", "9,455 buyers", "287", "$427.4M"], ["Aug '28", "Seller analytics", "28 CFO partners", "307 sellers", "307", "$459.5M"], ["Sep '28", "Buyer app", "Insurance + facility review", "EBITDA $1.1M", "327", "$491.2M"], ["Oct '28", "Platform v2", "51 partners live", "346 sellers · $524M GMV", "346", "$523.6M"]];

const MonthGrid = ({ n, eyebrow, title, rows }) => (
  <G n={n} shift={16} eyebrow={eyebrow} size={28} title={title}>
    {["SHIPS", "CHANNEL", "KPI HIT"].map((l, i) => (
      <Mono key={l} size={8.5} color="#7d8f85" style={{ position: "absolute", left: 43.2, top: 174 + i * 97 }}>{l}</Mono>
    ))}
    <Mono size={7.5} color="#7d8f85" style={{ position: "absolute", left: 43.2, top: 433 }}>SELLERS</Mono>
    <Mono size={7.5} color="#7d8f85" style={{ position: "absolute", left: 43.2, top: 451 }}>GMV / MO</Mono>
    {rows.map((r, i) => (
      <div key={r[0]} className="an" style={{ position: "absolute", left: 132 + i * 65.3, top: 112, width: 62, "--d": `${200 + i * 70}ms` }}>
        <Mono size={8} color={i === 11 ? LIME : "#9fb0a6"} ls="0.06em">{r[0]}</Mono>
        {[1, 2, 3].map((k) => (
          <div key={k} style={{ position: "absolute", top: 36 + (k - 1) * 97, fontSize: 8.5, color: k === 3 ? "#dfe8e2" : "#8ea096", lineHeight: "11px" }}>{r[k]}</div>
        ))}
        <div className="h" style={{ position: "absolute", top: 318, fontSize: 8, color: i === 11 ? LIME : "#fff" }}>{r[4]}</div>
        <div className="m" style={{ position: "absolute", top: 337, fontSize: 7.5, color: "#8ea096" }}>{r[5]}</div>
      </div>
    ))}
  </G>
);

const S19 = () => <MonthGrid n={19} eyebrow="MONTH-BY-MONTH ROADMAP · YEAR 1" rows={Y1}
  title={<>Year 1, month by month: <span className="lime">from BRND alone to 116 sellers.</span></>} />;
const S20 = () => <MonthGrid n={20} eyebrow="MONTH-BY-MONTH ROADMAP · YEAR 2" rows={Y2}
  title={<>Year 2: partners at scale, <span className="lime">$8.3M of EBITDA.</span></>} />;

const MLAB = M.months.map((m, i) => (i % 2 === 0 ? m.replace("'", "\n'") : ""));
const MLAB_LINE = M.months.map((m, i) => (i % 4 === 0 || i === 23 ? m : ""));

/* 21 — sellers & buyers */
const S21 = () => (
  <G n={21} eyebrow="24 MONTHS · SELLERS AND BUYERS" size={30}
    title={<>5 sellers to 346. <span className="lime">62 buyers to 11,535.</span></>}>
    <Mono size={9} color="#7d8f85" style={{ position: "absolute", left: 43.2, top: 114 }}>ACTIVE SELLERS BY PLAN, END OF MONTH</Mono>
    <StackBars x={75} y={150} w={495} h={275} max={400} ticks={[0, 100, 200, 300, 400]} fmtTick={(t) => `${t}`}
      labels={MLAB}
      series={[
        { n: "Starter", v: M.starter, c: GREEN },
        { n: "Growth", v: M.growth, c: GREEN_2 },
        { n: "Pro", v: M.pro, c: OFFWHITE },
        { n: "Enterprise + BRND", v: M.ent, c: LIME },
      ]} />
    <Legend style={{ position: "absolute", left: 200, top: 130 }} items={[{ n: "Starter", c: GREEN }, { n: "Growth", c: GREEN_2 }, { n: "Pro", c: OFFWHITE }, { n: "Enterprise + BRND", c: LIME }]} />
    <Row style={{ position: "absolute", left: 630, top: 128, gap: 18, alignItems: "flex-start" }}>
      {[["SELLERS · OCT ’28", 346, "Oct ’27: 116", (v) => Math.round(v)], ["BUYERS · OCT ’28", 11535, "Oct ’27: 3,555", (v) => Math.round(v).toLocaleString()]].map(([k, to, sub, fmt], i) => (
        <div key={k} className="an" style={{ width: 140, "--d": `${220 + i * 120}ms` }}>
          <Mono size={8} color="#7d8f85">{k}</Mono>
          <div className="h lime" style={{ fontSize: 24, marginTop: 10 }}><Count to={to} delay={320 + i * 120} fmt={fmt} /></div>
          <div style={{ fontSize: 9.5, color: "#8ea096", marginTop: 12 }}>{sub}</div>
        </div>
      ))}
    </Row>
    <div className="an" style={{ position: "absolute", left: 630, top: 222, fontSize: 9.5, color: "#dfe8e2", "--d": "520ms" }}>
      <b className="lime">44%</b> of new sellers partner-sourced by M24
    </div>
    <Mono size={9} color="#7d8f85" style={{ position: "absolute", left: 612, top: 266 }}>ACTIVE BUYERS, END OF MONTH</Mono>
    <LineChart x={648} y={292} w={262} h={132} data={M.buyers} max={14000} ticks={[0, 7000, 14000]}
      fmtTick={(t) => (t ? `${t / 1000}K` : "0")} gradId="gtmBuyers" d={420}
      labels={M.months.map((m, i) => (i === 0 || i === 12 || i === 23 ? m : ""))} />
  </G>
);

/* 22 — GMV and revenue */
const MIX = [
  ["Subscriptions", "$4.30M · 21%", 4.3, OFFWHITE],
  ["Payments & payouts", "$4.32M · 21%", 4.32, GREEN_2],
  ["Financing", "$6.61M · 33%", 6.61, LIME],
  ["Shipping", "$4.61M · 23%", 4.61, GREEN],
  ["Dropship", "$186K · 1%", 0.186, "#5f7068"],
  ["Insurance + apps", "$218K · 1%", 0.218, "#8c9d94"],
];
const S22 = () => (
  <G n={22} eyebrow="24 MONTHS · GMV AND REVENUE" size={30}
    title={<>$2.97M revenue in Year 1. <span className="lime">$17.27M in Year 2.</span></>}>
    <Row style={{ position: "absolute", left: 54, top: 118, width: 540 }}>
      {[["$4.2B", "Year-2 GMV"], ["0.4%", "take rate, Year 2"], ["$5.4M", "SaaS ARR, Oct ’28"], ["$2.2M", "revenue in Oct ’28"]].map(([v, l], i) => (
        <div key={l} className="an" style={{ width: 139, "--d": `${190 + i * 100}ms` }}>
          <div className="h" style={{ fontSize: 20 }}>{v}</div>
          <div className="m" style={{ fontSize: 9, color: "#8ea096", marginTop: 10, letterSpacing: "0.06em" }}>{l}</div>
        </div>
      ))}
    </Row>
    <Mono size={9} color="#7d8f85" style={{ position: "absolute", left: 43.2, top: 204 }}>MONTHLY REVENUE, NOV ’26 – OCT ’28</Mono>
    <LineChart x={100} y={226} w={470} h={200} data={M.revenue} max={2500000} ticks={[0, 500000, 1000000, 1500000, 2000000, 2500000]}
      fmtTick={(t) => `$${Math.round(t / 1000).toLocaleString()}K`} labels={MLAB_LINE} d={320} gradId="gtmRev"
      marks={[{ i: 11, label: "$663K", dx: -56, dy: -14 }, { i: 23, label: "$2.2M", dx: -46, dy: -16 }]} />
    <Mono size={9} color="#7d8f85" style={{ position: "absolute", left: 619.2, top: 118 }}>REVENUE MIX, MONTHS 1–24</Mono>
    <svg width="330" height="190" style={{ position: "absolute", left: 600, top: 138 }}>
      <Donut cx={165} cy={92} r={64} w={30} data={MIX.map(([, , v, c]) => ({ v, c }))} delay={300} />
      <text x={165} y={97} textAnchor="middle" className="h" style={{ fontSize: 15, fill: "#fff" }}>$20.24M</text>
    </svg>
    <div style={{ position: "absolute", left: 637.2, top: 332, width: 280 }}>
      {MIX.map(([n, v, , c], i) => (
        <Row key={n} className="an" style={{ justifyContent: "space-between", height: 22.3, "--d": `${420 + i * 80}ms` }}>
          <Row style={{ gap: 9 }}><Dot c={c} s={7} /><span style={{ fontSize: 9.5, color: "#dfe8e2" }}>{n}</span></Row>
          <span className="m" style={{ fontSize: 9, color: "#9fb0a6" }}>{v}</span>
        </Row>
      ))}
    </div>
  </G>
);

/* 23 — P&L and cash */
const PL = [
  ["Revenue", "$2.97M", "$17.27M"], ["Cost of revenue", "($1.06M)", "($5.44M)"],
  ["Gross profit", "$1.91M", "$11.84M"], ["Gross margin", "64%", "69%"],
  ["Opex", "($2.00M)", "($3.50M)"], ["EBITDA", "($94K)", "$8.34M"],
  ["Reserve-line interest", "($110K)", "($481K)"], ["Net income", "($204K)", "$7.86M"],
  ["Free cash, year-end", "$2.30M", "$10.15M"],
];
const S23 = () => (
  <G n={23} eyebrow="24 MONTHS · P&L AND CASH" size={30}
    title={<>EBITDA-positive in Jun ’27. <span className="lime">Cash never below $1.8M.</span></>}>
    <div style={{ position: "absolute", left: 61.2, top: 132, width: 330 }}>
      <Row style={{ justifyContent: "space-between" }}>
        <Mono size={9} color="#7d8f85">P&amp;L SUMMARY</Mono>
        <Row style={{ gap: 32 }}><Mono size={8.5} color="#7d8f85">YEAR 1</Mono><Mono size={8.5} color={LIME}>YEAR 2</Mono></Row>
      </Row>
      {PL.map(([k, a, b], i) => (
        <Row key={k} className="an" style={{ height: 33, borderBottom: "1px solid rgba(255,255,255,.05)", "--d": `${220 + i * 65}ms` }}>
          <span style={{ fontSize: 11, color: "#cfdcd4", flex: 1 }}>{k}</span>
          <span style={{ fontSize: 11, width: 74, textAlign: "right", color: "#9fb0a6" }}>{a}</span>
          <span className="h" style={{ fontSize: 11, width: 76, textAlign: "right", color: i >= 5 ? LIME : "#fff" }}>{b}</span>
        </Row>
      ))}
    </div>
    <Row style={{ position: "absolute", left: 435.6, top: 120, width: 492, alignItems: "flex-start" }}>
      {[["−$128K", "peak monthly burn · Nov ’26"], ["Jun ’27", "first EBITDA-positive month", true], ["$690K", "peak cumulative burn · May ’27"]].map(([v, l, lime], i) => (
        <div key={l} className="an" style={{ width: 164, paddingRight: 6, "--d": `${240 + i * 110}ms` }}>
          <div className={`h ${lime ? "lime" : ""}`} style={{ fontSize: 19 }}>{v}</div>
          <div className="m" style={{ fontSize: 8.5, color: "#8ea096", marginTop: 11, letterSpacing: "0.05em", lineHeight: "11px" }}>{l}</div>
        </div>
      ))}
    </Row>
    <Mono size={8.5} color="#7d8f85" style={{ position: "absolute", left: 424.8, top: 200 }}>FREE CASH BY MONTH · $2.5M EQUITY IN M1 · RESERVE FUNDED BY DEBT</Mono>
    <LineChart x={478} y={222} w={432} h={204} data={M.cash} max={12000000} ticks={[0, 2000000, 4000000, 6000000, 8000000, 10000000, 12000000]}
      fmtTick={(t) => `$${(t / 1e6).toFixed(1)}M`} labels={MLAB_LINE} d={340} gradId="gtmCash"
      marks={[{ i: 6, label: "low $1.8M", c: CORAL, dx: -22, dy: 22 }]} />
  </G>
);

/* 24 — unit economics */
const UE = [
  { k: "STARTER · $99 PLAN", v: "$1K", gp: "$880", gm: "69%", ch: "4.0%", ltv: "$22K" },
  { k: "GROWTH · $599 PLAN", v: "$6K", gp: "$4K", gm: "69%", ch: "2.5%", ltv: "$176K" },
  { k: "ENTERPRISE · $3,999 PLAN", v: "$28K", gp: "$19K", gm: "69%", ch: "1.5%", ltv: "$1.3M" },
];
const S24 = () => (
  <G n={24} shift={10} eyebrow="UNIT ECONOMICS · PER SELLER" size={28}
    title={<>Per seller: $1K to $28K a month. <span className="lime">CAC pays back in &lt;2 months.</span></>}>
    {UE.map((u, i) => (
      <div key={u.k} style={{ position: "absolute", left: 61.2, top: 0, width: 0 }} />
    ))}
    <Row style={{ position: "absolute", left: 61.2, top: 128, gap: 14 }}>
      {UE.map((u, i) => (
        <Card key={u.k} d={190 + i * 110} accent={i === 2} style={{ width: 191, height: 302, padding: "16px 18px" }}>
          <Mono size={8} color={i === 2 ? LIME : "#7d8f85"} ls="0.09em">{u.k}</Mono>
          <div className="h" style={{ fontSize: 32, marginTop: 12 }}>{u.v}</div>
          <div style={{ fontSize: 9.5, color: "#8ea096", marginTop: 12 }}>revenue / seller / month</div>
          <div className="vpd-rule" style={{ margin: "16px 0 4px" }} />
          {[["Gross profit / mo", u.gp], ["Gross margin", u.gm], ["Seller churn / mo", u.ch], ["LTV (gross profit)", u.ltv]].map(([k, v], j) => (
            <KV key={k} k={k} v={v} d={320 + i * 110 + j * 60} kSize={10} size={12} lime={j === 3} />
          ))}
        </Card>
      ))}
    </Row>
    <div style={{ position: "absolute", left: 684, top: 128, width: 233 }}>
      <Mono size={9} color={LIME}>BLENDED · MONTH 24</Mono>
      {[["CAC per seller, fully loaded", "$3K", 13], ["CAC payback", "0.8 months · target < 12", 11.5], ["LTV : CAC", "31×", 13], ["Credit-loss reserve", "0.30% of financed GMV", 11.5], ["Net revenue retention", "85% · M12→M24 cohort", 11.5]].map(([k, v, s], i) => (
        <div key={k} className="an" style={{ marginTop: i ? 29 : 22, "--d": `${420 + i * 95}ms` }}>
          <div style={{ fontSize: 10.5, color: "#8ea096" }}>{k}</div>
          <div className="h" style={{ fontSize: s, marginTop: 8, color: i === 4 ? LIME : "#fff" }}>{v}</div>
        </div>
      ))}
      <div style={{ marginTop: 14, height: 9, borderRadius: 999, background: "#122a22", overflow: "hidden" }}>
        <div className="gw" style={{ height: 9, borderRadius: 999, background: LIME, "--w": "85%", "--d": "980ms" }} />
      </div>
    </div>
  </G>
);

/* 25 — KPI scorecard */
const KPI = [
  ["Monthly GMV · north star", "PLAN", ["$15.7M", "$39.3M", "$159M", "$328M", "$524M"]],
  ["Monthly revenue", "PLAN", ["$50K", "$150K", "$663K", "$1.4M", "$2.2M"]],
  ["Active sellers", "PLAN", ["16", "34", "116", "227", "346"]],
  ["Active buyers", "PLAN", ["357", "885", "3,555", "7,280", "11,535"]],
  ["Partners live", "PLAN", ["1", "10", "23", "43", "51"]],
  ["Take rate (rev ÷ GMV)", "DERIVED", ["0.3%", "0.4%", "0.4%", "0.4%", "0.4%"]],
  ["Gross margin", "PLAN", ["39%", "64%", "67%", "68%", "69%"]],
  ["GMV on terms", "ASSUMPTION", ["8%", "15%", "25%", "25%", "25%"]],
  ["SaaS ARR", "PLAN", ["$120K", "$460K", "$1.7M", "$3.5M", "$5.4M"]],
  ["Bank reserve · debt-funded", "PLAN", ["$181K", "$452K", "$1.8M", "$3.8M", "$6.0M"]],
  ["CAC payback", "PLAN", ["2 mo", "1 mo", "1 mo", "1 mo", "1 mo"]],
  ["Net revenue retention", "M12→M24", ["—", "—", "—", "—", "85%"]],
];
const MS = ["M3", "M6", "M12", "M18", "M24"];
const S25 = () => (
  <G n={25} eyebrow="KPI SCORECARD · TARGETS" size={30}
    title={<>The scorecard: <span className="lime">plan targets for months 3, 6, 12, 18 and 24.</span></>}>
    {KPI.map((k, i) => (
      <div key={k[0]} className="an" style={{ position: "absolute", left: 54 + (i % 4) * 220.3, top: 128 + Math.floor(i / 4) * 116.5, width: 196, "--d": `${180 + i * 55}ms` }}>
        <Row style={{ justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontSize: 10.5, color: "#dfe8e2" }}>{k[0]}</span>
          <span className="m" style={{ fontSize: 6.5, color: "#5c6e65" }}>{k[1]}</span>
        </Row>
        <Row style={{ marginTop: 14 }}>
          {k[2].map((v, j) => (
            <div key={j} style={{ width: 39.6 }}>
              <div className="m" style={{ fontSize: 7, color: "#5c6e65" }}>{MS[j]}</div>
              <div className="h" style={{ fontSize: 9.5, marginTop: 7, color: j === 4 ? LIME : "#fff" }}>{v}</div>
            </div>
          ))}
        </Row>
      </div>
    ))}
  </G>
);

/* 26 — milestones */
const MILE = [
  ["BY MONTH 1", "Live with real orders", "BRND + beta · 5 sellers · 62 buyers"],
  ["BY MONTH 3", "Network + financing on", "16 sellers · 357 buyers · terms live"],
  ["BY MONTH 6", "A dense niche", "34 sellers · $39.3M GMV / mo · partners on"],
  ["BY MONTH 12", "Partner engine works", "116 sellers · $1.7M ARR · 36% via partners"],
  ["BY MONTH 24", "Profitable at scale, raise the A", "$524M GMV / mo · $5.4M ARR · 346 sellers"],
];
const S26 = () => (
  <G n={26} eyebrow="MILESTONES TO THE SERIES A" size={30}
    title={<>What Verqo must prove before the Series A, <span className="lime">and when.</span></>}>
    <Row style={{ position: "absolute", left: 43.2, top: 124, width: 873.6 }}>
      {["Day 1", "M3", "M6", "M9", "M12", "M15", "M18", "M21", "M24"].map((m, i) => (
        <span key={m} className="m an" style={{ width: 103.5, fontSize: 8.5, color: [0, 1, 2, 4, 8].includes(i) ? LIME : "#43544b", "--d": `${180 + i * 50}ms` }}>{m}</span>
      ))}
    </Row>
    <div className="gw" style={{ position: "absolute", left: 50, top: 160, height: 1, background: "rgba(255,255,255,.16)", "--w": "860px", "--d": "320ms" }} />
    {MILE.map(([k, t, s], i) => (
      <div key={k} className="an" style={{ position: "absolute", left: 57.6 + i * 176.4, top: 180, width: 160, "--d": `${380 + i * 110}ms` }}>
        <div style={{ width: 13, height: 13, borderRadius: 999, background: i === 4 ? LIME : OFFWHITE, marginTop: -26, boxShadow: i === 4 ? "0 0 0 4px rgba(220,255,58,.16)" : "none" }} />
        <Mono size={9} color={i === 4 ? LIME : "#7d8f85"} style={{ marginTop: 60 }}>{k}</Mono>
        <div className="h" style={{ fontSize: 16, marginTop: 14, lineHeight: "20px" }}>{t}</div>
        <div style={{ fontSize: 10.5, color: "#9fb0a6", marginTop: 26, lineHeight: "14.5px" }}>{s}</div>
      </div>
    ))}
    <Row className="an" style={{ position: "absolute", left: 43.2, top: 436, gap: 10, "--d": "960ms" }}>
      <span className="h lime" style={{ fontSize: 12 }}>The ask:</span>
      <span style={{ fontSize: 12, color: "#cfdcd4" }}>$5M = $2.5M equity + $2.5M reserve line (debt). Burn peaks at $690K; free cash never below $1.8M.</span>
    </Row>
  </G>
);

/* 27 — competitive positioning */
const CROWS = [
  ["Buyers + sellers natively", ["y", "y", "n", "n", "n"]],
  ["Seller paid day 1", ["y", "y", "n", "n", "p"]],
  ["Buyer credit, insured", ["y", "p", "n", "n", "p"]],
  ["Shipping built in", ["y", "y", "p", "n", "n"]],
  ["Banking at the core", ["y", "n", "n", "n", "n"]],
  ["Commission on the order", ["none", "15–25%", "none", "none", "none"]],
];
const CCOLS = ["Verqo", "Faire", "Shopify", "NuORDER", "TreviPay"];
const CMark = ({ k }) => {
  if (k === "y") return <Dot c={LIME} s={11} />;
  if (k === "p") return <span style={{ width: 11, height: 11, borderRadius: 999, background: "linear-gradient(90deg,#c6d4cb 50%,transparent 50%)", border: "1.2px solid #c6d4cb", display: "block" }} />;
  if (k === "n") return <span style={{ width: 11, height: 1.6, background: "#45574e", display: "block" }} />;
  return <span style={{ fontSize: 8.5, color: "#9fb0a6" }}>{k}</span>;
};
const CMAP = [
  ["Faire", 300, 74],
  ["Tundra · Abound (closed)", 96, 96],
  ["NuORDER", 112, 196],
  ["Shopify B2B", 232, 222],
  ["TreviPay · Resolve", 360, 244],
  ["Pepper · Choco · BlueCart", 70, 266],
];
const S27 = () => (
  <G n={27} eyebrow="COMPETITIVE POSITIONING" size={30}
    title={<>Everyone owns a slice. <span className="lime">Verqo owns the flow.</span></>}>
    <Mono size={8} color="#5c6e65" style={{ position: "absolute", left: 24, top: 430, transform: "rotate(-90deg)", transformOrigin: "left top", whiteSpace: "nowrap" }}>
      ONE SIDE ←→ BOTH BUYERS AND SELLERS
    </Mono>
    <div className="vpd-card an" style={{ position: "absolute", left: 43.2, top: 132, width: 560, height: 310, padding: 0, overflow: "hidden", "--d": "200ms" }}>
      {/* the quadrant Verqo plays in */}
      <div className="an-s" style={{ position: "absolute", left: 280, top: 0, width: 280, height: 155, background: "rgba(220,255,58,.09)", borderLeft: `1px solid rgba(220,255,58,.35)`, borderBottom: `1px solid rgba(220,255,58,.35)`, "--d": "320ms" }} />
      <div className="gw" style={{ position: "absolute", left: 0, top: 155, height: 1, background: "rgba(255,255,255,.12)", "--w": "560px", "--d": "360ms" }} />
      <div className="an" style={{ position: "absolute", left: 280, top: 0, width: 1, height: 310, background: "rgba(255,255,255,.12)", "--d": "420ms" }} />
      {CMAP.map(([t, x, y], i) => (
        <Row key={t} className="an" style={{ position: "absolute", left: x, top: y, gap: 8, "--d": `${460 + i * 80}ms` }}>
          <span style={{ width: 9, height: 9, borderRadius: 999, background: "#cfdcd4", flex: "0 0 auto" }} />
          <span style={{ fontSize: 9.5, color: "#8ea096", whiteSpace: "nowrap" }}>{t}</span>
        </Row>
      ))}
      <Row className="an-s" style={{ position: "absolute", left: 404, top: 22, gap: 9, alignItems: "center", "--d": "880ms" }}>
        <span style={{ width: 24, height: 24, borderRadius: 7, background: LIME, display: "grid", placeItems: "center" }}>
          <svg viewBox="0 0 32 32" width={16} height={16} fill="none">
            <path d="M7 11 L15 23 L27 4" stroke={INK} strokeWidth={5.4} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <span className="h" style={{ fontSize: 13 }}>Verqo</span>
      </Row>
      <Mono size={8} color="#5c6e65" style={{ position: "absolute", left: 16, top: 286 }}>ORDERING ONLY</Mono>
      <Mono size={8} color="#5c6e65" style={{ position: "absolute", left: 300, top: 274, width: 240, lineHeight: "11px" }}>
        OWNS THE MONEY: PAYOUTS, CREDIT, BANKING
      </Mono>
    </div>
    <div className="vpd-card-a an" style={{ position: "absolute", left: 617, top: 132, width: 299.8, height: 310, padding: "16px 16px", "--d": "300ms" }}>
      <Row style={{ justifyContent: "flex-end", gap: 0, marginBottom: 6 }}>
        {CCOLS.map((c, i) => (
          <span key={c} className="m" style={{ width: 38, textAlign: "center", fontSize: 7, color: i === 0 ? LIME : "#8ea096" }}>{c}</span>
        ))}
      </Row>
      {CROWS.map(([label, cells], r) => (
        <Row key={label} className="an" style={{ height: 42, borderTop: "1px solid rgba(255,255,255,.06)", "--d": `${420 + r * 80}ms` }}>
          <span style={{ fontSize: 8.5, color: "#dfe8e2", flex: 1, lineHeight: "11px" }}>{label}</span>
          {cells.map((k, i) => (
            <span key={i} style={{ width: 38, display: "grid", placeItems: "center" }}><CMark k={k} /></span>
          ))}
        </Row>
      ))}
    </div>
  </G>
);

/* 28 — team */
const TEAM = [
  ["Z", "Zev Ostreicher", "Founder & CEO", "15 years in wholesale · founder of BRND DIRECT", "[Prior exit: company, year]"],
  ["D", "David Levy", "Head of Operations", "Onboarding · fulfilment · distributor playbook", "[Prior company / exit]"],
  ["B", "Basi Hershkowitz", "Sales Director", "Founder-led sales into the beauty network", "[Prior company / exit]"],
  ["D", "Dom Byno", "Data & Automation", "Catalog AI · PO parsing · underwriting data", "[Prior company / exit]"],
];
const S28 = () => (
  <G n={28} shift={16} eyebrow="TEAM" size={30} title={<>We didn’t study wholesale. <span className="lime">We run it.</span></>}>
    <Row style={{ position: "absolute", left: 43.2, top: 134, gap: 12.8 }}>
      {TEAM.map(([ini, n, r, a, b], i) => (
        <Card key={n} d={190 + i * 90} style={{ width: 209.4, height: 196, padding: "18px 18px" }}>
          <div style={{ width: 48, height: 48, borderRadius: 999, background: i === 0 ? LIME : "#16302a", display: "grid", placeItems: "center" }}>
            <span className="h" style={{ fontSize: 15, color: i === 0 ? INK : LIME }}>{ini}</span>
          </div>
          <div className="h" style={{ fontSize: 13.5, marginTop: 20 }}>{n}</div>
          <Mono size={9} color={LIME} style={{ marginTop: 8 }}>{r}</Mono>
          <div style={{ fontSize: 10, color: "#cfdcd4", marginTop: 14, lineHeight: "13.5px" }}>{a}</div>
          <div style={{ fontSize: 9.5, color: "#5c6e65", marginTop: 10 }}>{b}</div>
        </Card>
      ))}
    </Row>
    <Card d={560} accent style={{ position: "absolute", left: 43.2, top: 350, width: 873.6, height: 72, padding: "0 24px", display: "flex", alignItems: "center" }}>
      <div style={{ width: 250 }}>
        <Mono size={9} color={LIME}>FUNDING TO DATE</Mono>
        <div className="h" style={{ fontSize: 24, marginTop: 8 }}>Bootstrapped</div>
      </div>
      <div style={{ fontSize: 14, color: "#cfdcd4" }}>Built so far on the founder’s own money and investment from friends &amp; family.</div>
    </Card>
    <div className="an" style={{ position: "absolute", left: 43.2, top: 436, "--d": "700ms" }}>
      <Mono size={9} color="#7d8f85">RAILS NAMED ON VERQO.COM</Mono>
      <div style={{ fontSize: 10.5, color: "#dfe8e2", marginTop: 10 }}>U.S. Bank · Allianz Trade · Capec · Plaid · Middesk · Stripe</div>
    </div>
  </G>
);

/* 29 — the raise */
const USE = [
  ["Team & hiring", "core team + first hires, until revenue covers costs", "$835K", 835, LIME],
  ["GTM & partners", "partner bounties, Jewish niche, expo, marketing", "$38K", 38, "#5f7068"],
  ["Platform, banking & compliance", "bank + shipping program fees, infra, legal, KYB", "$320K", 320, GREEN_2],
  ["ACH dispute insurance", "premium + the part of claims we keep", "$62K", 62, "#8c9d94"],
  ["Fees & one-time", "facility interest and fee, closing, SOC 2", "$144K", 144, GREEN],
  ["Cushion & growth", "≈5 months of opex, or faster growth", "$1.10M", 1100, OFFWHITE],
];
const S29 = () => (
  <G n={29} eyebrow="THE RAISE · USE OF FUNDS" size={28}
    title={<>$5M seed = $2.5M equity + <span className="lime">$2.5M reserve facility (debt).</span></>}>
    <svg width="320" height="300" style={{ position: "absolute", left: 20, top: 150 }}>
      <Donut cx={160} cy={130} r={86} w={40} data={[{ v: 2.5, c: LIME }, { v: 2.5, c: GREEN }]} delay={240} />
      <text x={160} y={132} textAnchor="middle" className="h" style={{ fontSize: 26, fill: "#fff" }}>$5M</text>
      <text x={160} y={152} textAnchor="middle" style={{ fontSize: 10, fill: "#8ea096", fontFamily: "DM Sans" }}>seed</text>
    </svg>
    <div style={{ position: "absolute", left: 342, top: 126, width: 330 }}>
      <Row style={{ justifyContent: "space-between" }}>
        <Mono size={9} color={LIME}>EQUITY · $2.5M · RUNS THE COMPANY</Mono>
        <span className="m" style={{ fontSize: 10, color: "#8ea096" }}>50%</span>
      </Row>
      {USE.map(([n, s, v, , c], i) => (
        <Row key={n} className="an" style={{ height: 37.5, justifyContent: "space-between", alignItems: "flex-start", marginTop: i ? 0 : 14, "--d": `${300 + i * 80}ms` }}>
          <div>
            <div style={{ fontSize: 11, color: "#fff" }}>{n}</div>
            <div style={{ fontSize: 8.5, color: "#7d8f85", marginTop: 3 }}>{s}</div>
          </div>
          <span className="h" style={{ fontSize: 11 }}>{v}</span>
        </Row>
      ))}
      <Row style={{ justifyContent: "space-between", marginTop: 16 }}>
        <Mono size={9} color="#7d8f85">DEBT · $2.5M · HELD, NOT SPENT</Mono>
        <span className="m" style={{ fontSize: 10, color: "#8ea096" }}>50%</span>
      </Row>
      <Row className="an" style={{ justifyContent: "space-between", alignItems: "flex-start", marginTop: 14, "--d": "820ms" }}>
        <div>
          <div style={{ fontSize: 11, color: "#fff" }}>Reserve facility</div>
          <div style={{ fontSize: 8.5, color: "#7d8f85", marginTop: 3 }}>funds the bank reserve · comes back</div>
        </div>
        <span className="h lime" style={{ fontSize: 11 }}>$2.50M</span>
      </Row>
    </div>
    <Card d={360} accent style={{ position: "absolute", left: 684, top: 132, width: 232.8, height: 312, padding: "16px 18px" }}>
      <Mono size={9} color={LIME}>WHAT THE EQUITY BUYS</Mono>
      <div style={{ fontSize: 10.5, color: "#cfdcd4", marginTop: 14, lineHeight: "15px" }}>
        All costs to break-even (Jun ’27), plus a $1.1M cushion. Free cash never below $1.8M.
      </div>
      {[["Month 6", "34 sellers · $39.3M GMV / mo"], ["Month 12", "116 sellers · $1.7M SaaS ARR"], ["Month 24", "$524M GMV / mo · $8.3M Y2 EBITDA"]].map(([k, v], i) => (
        <div key={k} className="an" style={{ marginTop: i ? 26 : 30, "--d": `${520 + i * 110}ms` }}>
          <div className="h" style={{ fontSize: 13 }}>{k}</div>
          <div style={{ fontSize: 10, color: "#8ea096", marginTop: 9, lineHeight: "13px" }}>{v}</div>
        </div>
      ))}
    </Card>
  </G>
);

/* 30 — bank reserve */
const RISK = [
  ["ACH returns & disputes", "0.05% of ACH volume expected · Nacha unauthorized cap 0.5%"],
  ["Insurance pays the reserve back", "90% of claims above 0.03% of ACH volume a month"],
  ["Credit-loss reserve on terms", "0.30% of financed GMV · hard credit limits"],
  ["KYB & underwriting", "every seller and buyer verified before money moves"],
];
const S30 = () => (
  <G n={30} eyebrow="BANK RESERVE · RISK & PROTECTION" size={28}
    title={<>The bank reserve is borrowed, not spent. <span className="lime">And it is insured.</span></>}>
    <Mono size={9} color="#7d8f85" style={{ position: "absolute", left: 43.2, top: 118 }}>BANK RESERVE = RESERVE FACILITY DRAWN, END OF MONTH</Mono>
    <LineChart x={90} y={142} w={430} h={166} data={M.reserve} max={7000000} ticks={[0, 2000000, 4000000, 6000000]}
      fmtTick={(t) => `$${(t / 1e6).toFixed(1)}M`} labels={MLAB_LINE} d={300} gradId="gtmRes"
      marks={[{ i: 23, label: "$6.0M", dx: -44, dy: -14 }]} />
    <Row style={{ position: "absolute", left: 53.3, top: 360, width: 520, gap: 0 }}>
      {[["10% × 7 days", "held on ACH volume only"], ["RTP · FedNow · card", "no reserve"], ["$2.5M → $7.5M", "initial line → accordion"], ["Repaid", "as each hold releases"]].map(([t, s], i) => (
        <div key={t} className="an" style={{ width: 124, paddingRight: 12, "--d": `${520 + i * 100}ms` }}>
          <div className="h" style={{ fontSize: 13, lineHeight: "16px" }}>{t}</div>
          <div style={{ fontSize: 9.5, color: "#8ea096", marginTop: 16, lineHeight: "12.5px" }}>{s}</div>
        </div>
      ))}
    </Row>
    <Card d={320} accent style={{ position: "absolute", left: 597.6, top: 126, width: 319.2, height: 322, padding: "16px 18px" }}>
      <Mono size={9} color={LIME}>RISK &amp; PROTECTION</Mono>
      {RISK.map(([k, v], i) => (
        <div key={k} className="an" style={{ marginTop: i ? 26 : 22, "--d": `${440 + i * 110}ms` }}>
          <div className="h" style={{ fontSize: 13 }}>{k}</div>
          <div style={{ fontSize: 10, color: "#9fb0a6", marginTop: 9, lineHeight: "13.5px" }}>{v}</div>
        </div>
      ))}
    </Card>
  </G>
);

/* 31 — closer */
const S31 = () => (
  <G n={31} tagline="GO-TO-MARKET · 2026">
    <div style={{ position: "absolute", left: 43.2, top: 150, width: 820 }}>
      <h1 className="h an lime" style={{ fontSize: 64, "--d": "140ms" }}>Wholesale, instant.</h1>
      <div className="an" style={{ width: 82, height: 3, background: LIME, marginTop: 36, "--d": "260ms" }} />
      <div className="h an" style={{ fontSize: 17, marginTop: 36, "--d": "340ms" }}>Land our own buyers and the Jewish wholesale community.</div>
      <div className="h an" style={{ fontSize: 17, marginTop: 10, color: "#9fb0a6", "--d": "420ms" }}>Monetize every order. Let partners bring the next seller.</div>
      <div className="an" style={{ fontSize: 12, marginTop: 44, color: "#cfdcd4", "--d": "520ms" }}>
        Raising <b>$5M seed</b> · verqo.com · Zev Ostreicher, Founder &amp; CEO
      </div>
      <PitchLink style={{ marginTop: 26 }} />
    </div>
  </G>
);

export const PART_B = [...PHASES.map((p, i) => () => <Phase p={p} idx={i} />), S19, S20, S21, S22, S23, S24, S25, S26, S27, S28, S29, S30, S31];
